import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Heart, Award, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Precision',
      description: 'Every detail matters. We ensure accuracy in every return and consultation.'
    },
    {
      icon: Heart,
      title: 'Care',
      description: 'Your financial well-being is our priority. We treat your taxes like our own.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We stay current with tax laws and best practices to deliver superior service.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'We build lasting relationships, supporting you year-round, not just tax season.'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-left duration-700">
              About Takeover Tax Inc.
            </h1>
            <p className="text-2xl text-teal-600 font-semibold mb-6 animate-in fade-in slide-in-from-left duration-700 delay-100">
              Where Expert Care Meets Your Financial Goals
            </p>
            <p className="text-lg text-gray-600 leading-relaxed animate-in fade-in slide-in-from-left duration-700 delay-200">
              At Takeover Tax Inc., we understand that taxes can be overwhelming. That's why we're here—to take over the stress and complexity, allowing you to focus on what truly matters. With a team of 10 certified tax professionals, we bring decades of combined experience and a personal touch to every client relationship.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Founded by Las Vegas native Tamika Harvey, Takeover Tax Inc. was born from a passion for helping people achieve financial success and peace of mind. As a mother of three and dedicated professional, Tamika understands the importance of balancing life's demands while staying on top of complex financial matters.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                With over 6 years of expertise in tax laws, IRS standards and regulations, and a special talent for maximizing deductions and credits, Tamika built Takeover Tax Inc. on a foundation of genuine care and professional excellence. Her belief is simple: tax preparation should be stress-free, personalized, and results-driven.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Today, Takeover Tax Inc. proudly serves clients nationwide with <strong>11 trusted partners located across 8 different states</strong>. This strategic presence allows us to provide personalized, localized service while maintaining the highest standards of expertise. Whether you're an individual, family, small business, or corporation, our team is ready to help you navigate tax complexities with confidence and achieve your financial goals.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=800&fit=crop"
                  alt="Takeover Tax Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-teal-600 text-white rounded-xl shadow-xl p-6">
                <div className="text-4xl font-bold">11</div>
                <div className="text-sm">Tax Professionals</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at Takeover Tax Inc.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 duration-300 bg-white">
                  <CardContent className="p-6 space-y-4 text-center">
                    <div className="h-16 w-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
                      <Icon className="h-8 w-8 text-teal-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900">Our Mission</h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            To provide exceptional, personalized tax services that empower our clients to achieve financial success and peace of mind. We're committed to staying at the forefront of tax legislation, delivering accurate results, and building lasting relationships based on trust and integrity.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="space-y-2">
              <div className="text-4xl font-bold text-teal-600">500+</div>
              <div className="text-gray-600">Clients Served</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-teal-600">98%</div>
              <div className="text-gray-600">Satisfaction Rate</div>
            </div>
            <div className="space-y-2">
              <div className="text-4xl font-bold text-teal-600">15+</div>
              <div className="text-gray-600">Years Combined Experience</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
